'use client'

import { ThumbDownAlt, ThumbDownAltOutlined, ThumbUpAlt, ThumbUpAltOutlined } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { useSetMessageFeedbackMutation } from '~/data/api/client/assistant'
import { logger } from '~/debug/logger'

type FeedbackControlsProps = {
  conversationId: string
  messageId: string
  /** The rating stored on the transcript, if the viewer rated this message before. */
  initialRating?: 'up' | 'down'
}

/** Thumbs up or down on one assistant reply. Choosing the current rating again clears it. */
export default function FeedbackControls({ conversationId, messageId, initialRating }: FeedbackControlsProps) {
  const t = useTranslations('features.AssistantV2')
  const [submitFeedback, { isLoading, isError }] = useSetMessageFeedbackMutation()
  const [rating, setRating] = useState<'up' | 'down' | null>(initialRating ?? null)

  const rate = async (choice: 'up' | 'down') => {
    const next = rating === choice ? null : choice
    try {
      await submitFeedback({ conversationId, messageId, messageFeedbackParams: { rating: next } }).unwrap()
      setRating(next)
    } catch (error) {
      logger.error('Failed to send assistant feedback', error)
    }
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Tooltip title={t('feedbackUp')}>
        <IconButton
          size="small"
          aria-label={t('feedbackUp')}
          aria-pressed={rating === 'up'}
          disabled={isLoading}
          onClick={() => void rate('up')}
        >
          {rating === 'up' ? <ThumbUpAlt fontSize="inherit" /> : <ThumbUpAltOutlined fontSize="inherit" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={t('feedbackDown')}>
        <IconButton
          size="small"
          aria-label={t('feedbackDown')}
          aria-pressed={rating === 'down'}
          disabled={isLoading}
          onClick={() => void rate('down')}
        >
          {rating === 'down' ? <ThumbDownAlt fontSize="inherit" /> : <ThumbDownAltOutlined fontSize="inherit" />}
        </IconButton>
      </Tooltip>
      {isError && (
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          {t('feedbackError')}
        </Typography>
      )}
    </Stack>
  )
}
